import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { createStore, useStore } from 'zustand';
import { useLocalStorageState } from '@/lib/hooks/hooks';
import { LOCAL_API_ORIGIN } from '@/lib/utils/localApiOrigin';

const INITIAL_RENDER_DATA = gql`
  query initialRenderData {
    currentUser {
      id
      firstName
      lastName
      email
    }
    connectedCompanies {
      id
      companyName
      companyType
      # Default TRUE — only an explicit opt-out is false. Read through @/lib/totes, never inline.
      # Fetched once at mount; use it only for cosmetics (see the note in that file).
      usesTotes
    }
    connectedCompanyPermissions {
      id
      resource
      actions
      permission
      companyId
    }
  }
`;

const RootContext = createContext();

export const useRootStore = (selector) => {
  const store = useContext(RootContext);
  if (!store) {
    throw new Error('Missing RootProvider');
  }
  return useStore(store, selector);
};

const getPermissionFunction =
  (connectedCompanyPermissions) => (resourceActionKey: string) => {
    const [resource, action] = resourceActionKey.split(':');
    return connectedCompanyPermissions.find(
      (permission: any) =>
        permission.resource === resource && permission.actions.includes(action),
    );
  };

export const RootProvider = ({ children }) => {
  const rootRef = useRef();
  const [selectedCompanyId, setSelectedCompanyId] = useLocalStorageState<string>(
    '',
    'selectedCompany'
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const { data, error, loading, refetch } = useQuery(INITIAL_RENDER_DATA, {
    fetchPolicy: 'network-only'
  });

  const submitLogout = async () => {
    try {
      await fetch(
        window.location.host.includes('localhost')
          ? `${LOCAL_API_ORIGIN}/api/logout`
          : '/api/logout',
        {
          credentials: 'include'
        }
      );
      refetch();
    } catch (e) {
      console.log(e);
    }
  };

  if (!rootRef.current) {
    rootRef.current = createStore((set) => ({
      selectedCompany: null,
      setSelectedCompanyId,
      companies: [],
      currentUser: data?.currentUser,
      refetch,
      logout: submitLogout,
      loading,
      getPermission: () => {},
    }));
  }

  useEffect(() => {
    if (!!data) {
      const { currentUser, connectedCompanies, connectedCompanyPermissions } = data || {};
      let updatedData = { currentUser };

      if (!!connectedCompanies?.length) {
        const selectedCompany =
          connectedCompanies.find((company) => company.id === selectedCompanyId) ||
          connectedCompanies[0];
        updatedData = {
          ...updatedData,
          companies: connectedCompanies,
          selectedCompany,
          getPermission: getPermissionFunction(
            (connectedCompanyPermissions || []).filter(
              (permission) => permission.companyId === selectedCompany.id,
            ),
          ),
        };
        if (!selectedCompanyId) setSelectedCompanyId(connectedCompanies?.[0]?.id);
      }
      setIsInitialized(true);
      rootRef.current.setState(updatedData);
    }
  }, [data]);

  useEffect(() => {
    rootRef.current.setState({ loading });
  }, [loading]);

  if (loading || !isInitialized) {
    return <div>Loading...</div>;
  }

  return <RootContext.Provider value={rootRef.current}>{children}</RootContext.Provider>;
};
